import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { getFieldComponent } from '../FieldTypes/registry';
import { evaluateLiveValidation } from '../validator';
import { FIELD_TYPE, WIDE_FIELD_TYPES, CHECKBOX_FIELD_TYPES, SELF_LAYOUT_FIELD_TYPES } from '../constants';
import './styles.scss';
import { testId } from '../../../utils/testIds';

function DynamicField({ config, fieldState, renderMode, renderType }) {
  const { control, getValues, setError, clearErrors, formState: { errors } } = useFormContext();

  if (!fieldState || !fieldState.visible) return null;

  const FieldComponent = getFieldComponent(config.fieldType);

  if (!FieldComponent) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[FieldEngine] No component registered for field type "${config.fieldType}" (field: "${config.fieldKey}")`
      );
    }
    return null;
  }

  const error   = errors[config.fieldKey];
  const isMulti = config.fieldType === FIELD_TYPE.MULTIPLE_CHOICE_LIST;

  const isWide     = WIDE_FIELD_TYPES.includes(config.fieldType);
  const isCheckbox = CHECKBOX_FIELD_TYPES.includes(config.fieldType);
  const isSelfLayout = SELF_LAYOUT_FIELD_TYPES.includes(config.fieldType)

  if(isSelfLayout) {
    return (
        <div className={fieldState.disabled ? 'disabled' : undefined} data-test={testId('field', config.fieldKey)}>
          <Controller
            name={config.fieldKey}
            control={control}
            render={({field:rhfField}) => (
                <FieldComponent
                  config={config}
                  value={rhfField.value}
                  onChange={(val) => {
                    rhfField.onChange(val);
                    const liveMessage = evaluateLiveValidation(config, val, getValues(), fieldState);
                    if (liveMessage) {
                      setError(config.fieldKey, { type: 'live', message: liveMessage });
                    } else {
                      clearErrors(config.fieldKey);
                    }
                  }}
                  onBlur={rhfField.onBlur}
                  disabled={fieldState.disabled}
                  required={fieldState.required}
                  options={fieldState.options}
                  error={error?.message ?? null}
                  renderMode={renderMode}
                  renderType={renderType}
                  multi={isMulti}
                />
            )}
          />
        </div>
    )
  }

  return (
    <div
      className={`formGroup${isWide ? ' field-engine-wide' : ''}${fieldState.disabled ? ' disabled' : ''}`}
      data-test={testId('field', config.fieldKey)}
    >
      {isCheckbox ? (
        <div className="wth-2" />
      ) : (
        <div className="wth-2">
          <label htmlFor={config.fieldKey}>
            {config.fieldGroup || config.label}
            {fieldState.required && <sup>*</sup>}
          </label>
        </div>
      )}

      <div className={`wth-${config.fieldSettings?.colWidth ?? 4}`}>
        <Controller
          name={config.fieldKey}
          control={control}
          render={({ field: rhfField }) => {
            const rightAdornment = config.fieldSettings?.rightAdornment;
            const fieldNode = (
              <FieldComponent
                config={config}
                value={rhfField.value}
                onChange={(val) => {
                  rhfField.onChange(val);
                  const liveMessage = evaluateLiveValidation(config, val, getValues(), fieldState);
                  if (liveMessage) {
                    setError(config.fieldKey, { type: 'live', message: liveMessage });
                  } else {
                    clearErrors(config.fieldKey);
                  }
                }}
                onBlur={rhfField.onBlur}
                disabled={fieldState.disabled}
                required={fieldState.required}
                options={fieldState.options}
                error={error?.message ?? null}
                renderMode={renderMode}
                renderType={renderType}
                multi={isMulti}
              />
            );
            if (typeof rightAdornment !== 'function') return fieldNode;
            return (
              <div className="field-engine-adornment-row">
                <div className="field-engine-adornment-field">{fieldNode}</div>
                {rightAdornment({
                  value: rhfField.value,
                  onChange: rhfField.onChange,
                  disabled: fieldState.disabled,
                })}
              </div>
            );
          }}
        />

        {error?.message && (
          <span className="field-engine-error" role="alert">{error.message}</span>
        )}

        {config.description && config.fieldType !== FIELD_TYPE.CHECKBOX && (
          <p className="formHelperText">{config.description}</p>
        )}
      </div>
    </div>
  );
}

export default DynamicField;
